import { Link } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, Calendar, CreditCard, Loader2, Settings, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBilling } from "@/lib/hooks/useBilling";
import { planLabel, isPaidPlan, buildPlanCatalog } from "@/lib/billing-plans";
import type { Tenant } from "@/lib/types";
import type { SubscriptionState } from "@/lib/hooks/useTenant";
import { cn } from "@/lib/utils";

interface BillingStatusCardProps {
  tenant: Tenant;
  subscription: SubscriptionState | null;
}

/** Le o Price ID do plano Pro mensal para o CTA de upgrade rapido.
 *  Null quando o env nao foi configurado (desabilita o botao direto). */
function readProMonthPriceId(): string | null {
  const catalog = buildPlanCatalog();
  return catalog.find((e) => e.plan === "pro")?.prices.month.priceId ?? null;
}

/** Card de estado da assinatura: plano atual, proximo vencimento, cancelamento
 *  agendado, CTA de upgrade direto para Free e Customer Portal para assinantes. */
export function BillingStatusCard({ tenant, subscription }: BillingStatusCardProps): JSX.Element {
  const { startCheckout, openPortal, loading, error } = useBilling();
  const paid = isPaidPlan(tenant.plan);
  const cancelScheduled = subscription?.cancelAtPeriodEnd === true;
  const renewLabel = subscription?.currentPeriodEnd ?? tenant.pro_until;
  const proMonthPriceId = readProMonthPriceId();

  return (
    <section
      className="rounded-xl border border-border bg-card overflow-hidden"
      aria-labelledby="section-billing"
    >
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
        <CreditCard size={14} className="text-muted-foreground" />
        <h2
          id="section-billing"
          className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
        >
          Plano e assinatura
        </h2>
      </div>

      <div className="p-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground font-medium">Plano atual</p>
            <PlanBadge plan={tenant.plan} />
          </div>

          {!paid && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="gold"
                size="sm"
                disabled={!proMonthPriceId || loading}
                onClick={() => proMonthPriceId && void startCheckout(proMonthPriceId)}
                aria-label="Fazer upgrade para o plano PRO mensal"
              >
                {loading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Zap size={14} />
                )}
                Fazer upgrade
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/app/plans">
                  <Sparkles size={14} />
                  Ver planos
                  <ArrowRight size={13} />
                </Link>
              </Button>
            </div>
          )}
        </div>

        {!paid && (
          <UpgradeBenefitsList />
        )}

        {paid && renewLabel && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground font-medium">
              {cancelScheduled ? "Acesso até" : "Renova em"}
            </p>
            <div className="flex items-center gap-2.5">
              <Calendar size={14} className="text-muted-foreground" />
              <p className="text-sm font-medium text-foreground font-mono">
                {formatDate(renewLabel)}
              </p>
            </div>
          </div>
        )}

        {cancelScheduled && (
          <InlineNotice tone="warning">
            Cancelamento agendado. Você mantém o acesso PRO até o fim do ciclo atual.
          </InlineNotice>
        )}

        {error && (
          <InlineNotice tone="danger" icon={<AlertCircle size={15} />}>
            {error}
          </InlineNotice>
        )}

        {paid && (
          <div className="flex flex-wrap gap-2.5">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void openPortal()}
              disabled={loading}
              aria-label="Gerenciar ou cancelar assinatura no portal Stripe"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />}
              Gerenciar assinatura
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/plans">Comparar planos</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

/** Lista compacta de beneficios exibida apenas para usuarios Free,
 *  para contextualizar o upgrade sem precisar navegar para /plans. */
function UpgradeBenefitsList(): JSX.Element {
  const items = [
    "Grupos e canais ilimitados",
    "Links ilimitados",
    "Vitrine e link na bio",
    "Analytics completo",
  ] as const;

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 space-y-2">
      <p className="text-xs font-semibold text-primary uppercase tracking-[0.07em]">
        O que o PRO destrava
      </p>
      <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {items.map((item) => (
          <li key={item} className="flex items-center gap-1.5 text-xs text-foreground/80">
            <Sparkles size={11} className="shrink-0 text-primary" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Badge de plano com 3 estados: Free, PRO, PRO + IA. */
export function PlanBadge({ plan }: { plan: string }): JSX.Element {
  const paid = isPaidPlan(plan);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border",
        paid
          ? "bg-primary/15 text-primary border-primary/25"
          : "bg-muted text-muted-foreground border-transparent",
      )}
    >
      {planLabel(plan)}
    </span>
  );
}

function InlineNotice({
  children,
  icon,
  tone,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone: "warning" | "danger";
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "warning"
          ? "border-warning/30 bg-warning/10 text-warning"
          : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
